import { useMemo } from 'react';

import type { TaxonomyType } from '@revelio/data-access';

import { useTaxonomyList } from './useTaxonomyList';
import { useTaxonomyLatestVersion } from './useTaxonomyVersions';

export type ReferenceTaxonomy = {
  id: number;
  name: string;
  version: string;
  is_latest: boolean;
};

/**
 * Reference-taxonomy versions (list + latest) for taxonomies whose selection
 * is a plain numeric id (skill, activity). Role keeps its own adapter — its
 * selection is an object union with a different id shape.
 */
export const useReferenceTaxonomyVersions = ({
  taxonomyType,
  pause = false,
}: {
  taxonomyType: TaxonomyType;
  pause?: boolean;
}) => {
  const {
    taxonomies: referenceTaxonomies,
    hasData: hasReferenceTaxonomies,
    fetching: referenceFetching,
    error: referenceError,
  } = useTaxonomyList({
    taxonomyType,
    pause,
  });
  const {
    version: latestReferenceVersion,
    hasData: hasLatestReferenceVersion,
    fetching: latestReferenceFetching,
    error: latestReferenceError,
  } = useTaxonomyLatestVersion({
    taxonomyType,
    pause,
  });

  const mappedReferenceTaxonomies = useMemo(() => {
    if (!hasReferenceTaxonomies || !hasLatestReferenceVersion) {
      return undefined;
    }

    return referenceTaxonomies.map(
      (taxonomy): ReferenceTaxonomy => ({
        id: taxonomy.id,
        name: taxonomy.name,
        version: taxonomy.version,
        is_latest:
          latestReferenceVersion !== undefined &&
          latestReferenceVersion !== null &&
          taxonomy.version === latestReferenceVersion,
      })
    );
  }, [
    hasLatestReferenceVersion,
    hasReferenceTaxonomies,
    latestReferenceVersion,
    referenceTaxonomies,
  ]);

  return {
    taxonomies: mappedReferenceTaxonomies,
    loading:
      !pause &&
      (referenceFetching ||
        latestReferenceFetching ||
        (!hasReferenceTaxonomies && !referenceError) ||
        (!hasLatestReferenceVersion && !latestReferenceError)),
    error: referenceError ?? latestReferenceError,
  };
};

export const useCurrentReferenceTaxonomyVersion = ({
  taxonomyType,
  pause = false,
}: {
  taxonomyType: TaxonomyType;
  pause?: boolean;
}) => {
  const { taxonomies, loading, error } = useReferenceTaxonomyVersions({
    taxonomyType,
    pause,
  });

  const defaultVersion = taxonomies?.find(
    (taxonomy: ReferenceTaxonomy) => taxonomy.is_latest
  )?.version;

  const getSpecificTaxonomyVersion = (taxonomySelection: {
    taxonomyId?: number;
  }) => {
    if (taxonomySelection.taxonomyId) {
      const taxonomy = taxonomies?.find(
        (taxonomy: ReferenceTaxonomy) =>
          taxonomy.id === taxonomySelection.taxonomyId
      );
      return taxonomy?.version;
    }

    return defaultVersion;
  };

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!pause && !taxonomies && !error),
    error,
    isValidTaxonomyId: (taxonomyId: number) => {
      if (error) {
        console.error(`${taxonomyType} taxonomy versions are unknown`, error);
        return false;
      }
      return (
        taxonomies?.some(
          (taxonomy: ReferenceTaxonomy) => taxonomy.id === taxonomyId
        ) ?? false
      );
    },
    getSpecificTaxonomyVersion,
  };
};
